/**
 * JSON Schema → TypeBox translator for MCP tool input schemas.
 *
 * MCP tools declare their parameters as JSON Schema; pi-ai's AgentTool
 * expects a TypeBox `TSchema`. Real MCP servers stick to a small
 * subset of JSON Schema, so we translate that subset faithfully and
 * fall back to `Type.Any()` for anything exotic ($ref, oneOf, etc.)
 * rather than rejecting the tool entirely.
 *
 * Keeping this dependency-free of pi-agent-core — we only import
 * `Type` from the typebox re-export pi-ai provides.
 */

import { Type } from "@earendil-works/pi-ai";

export function jsonSchemaToTypeBox(schema) {
  if (!schema || typeof schema !== "object") return Type.Object({});
  return convert(schema);
}

function options(s) {
  const opts = {};
  if (typeof s.description === "string" && s.description) opts.description = s.description;
  if (typeof s.default !== "undefined") opts.default = s.default;
  return Object.keys(opts).length ? opts : undefined;
}

function convert(s) {
  // `enum` short-circuits regardless of type — every MCP tool we've
  // seen uses string enums, but typing is informational so a mixed
  // enum collapses to a Union of Literals just fine.
  if (Array.isArray(s.enum)) {
    if (s.enum.length === 1) return Type.Literal(s.enum[0], options(s));
    return Type.Union(s.enum.map((v) => Type.Literal(v)), options(s));
  }

  const t = Array.isArray(s.type) ? s.type[0] : s.type;
  switch (t) {
    case "object": {
      const properties = {};
      const required = new Set(Array.isArray(s.required) ? s.required : []);
      const propsIn = s.properties || {};
      for (const [k, v] of Object.entries(propsIn)) {
        const child = convert(v);
        properties[k] = required.has(k) ? child : Type.Optional(child);
      }
      return Type.Object(properties, options(s));
    }
    case "array":
      return Type.Array(convert(s.items || {}), options(s));
    case "string":
      return Type.String(options(s));
    case "number":
      return Type.Number(options(s));
    case "integer":
      return Type.Integer(options(s));
    case "boolean":
      return Type.Boolean(options(s));
    case "null":
      return Type.Null();
    default:
      // Untyped / $ref / oneOf / anyOf etc. Fall back to a permissive
      // shape so the tool stays usable. Models tend to produce
      // best-effort args anyway.
      return Type.Any(options(s));
  }
}
